// src/components/ShopSection.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import ProductCard from "./ProductCard";

import api from "../api/api";
import { ProductServices } from "../services/ProductsServices";
import { CategoriesServices } from "../services/CategoriesServices";
import { ShopPageBannerServices } from "../services/ShopPageBanner";

/* -------------------------------
   Helpers
--------------------------------- */
const toAbsoluteUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;

  let path = url.startsWith("/") ? url : `/${url}`;
  if (!path.startsWith("/storage/")) path = `/storage${path}`;

  const base = (api.defaults.baseURL || "").replace(/\/api\/?$/, "");
  return `${base}${path}`;
};

const money = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return `$${n.toFixed(2)}`;
};

const calcDiscountPercent = (regular, current) => {
  const r = Number(regular);
  const c = Number(current);
  if (!r || Number.isNaN(r) || Number.isNaN(c) || r <= 0) return null;
  if (c >= r) return null;
  return Math.round(((r - c) / r) * 100);
};

/* -------------------------------
   Pagination Item
--------------------------------- */
const PaginationItem = ({
  page,
  isActive = false,
  isArrow = false,
  icon = null,
  onClick,
}) => {
  return (
    <li className={`page-item ${isActive ? "active" : ""}`}>
      <button
        type="button"
        className="page-link h-64 w-64 flex-center text-md rounded-8 fw-medium text-neutral-600 border border-gray-100"
        onClick={onClick}
        style={{ background: "transparent" }}
      >
        {isArrow ? <i className={`ph-bold ${icon}`} /> : page}
      </button>
    </li>
  );
};

const ShopSection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const shopTopRef = useRef(null);

  const scrollToTop = () => {
    const el = shopTopRef.current;
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // UI
  const [grid, setGrid] = useState(false);
  const [activeSidebar, setActiveSidebar] = useState(false);

  // Data
  const [categories, setCategories] = useState([]);
  const [productsRaw, setProductsRaw] = useState([]);

  // ✅ Random shop banner (single)
  const [shopBanner, setShopBanner] = useState(null);
  const [loadingBanner, setLoadingBanner] = useState(false);

  // Loading / error
  const [loadingCats, setLoadingCats] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [error, setError] = useState(null);

  // Sort
  const [priceSort, setPriceSort] = useState(""); // "" | "low" | "high"

  // Pagination
  const [page, setPage] = useState(1);
  const PER_PAGE = 12;

  // ✅ Read all active filters from URL
  const selectedCategoryId = useMemo(() => {
    const v = searchParams.get("cat");
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [searchParams]);

  const searchQuery = useMemo(() => {
    return (searchParams.get("q") || "").trim().toLowerCase();
  }, [searchParams]);

  const locationFilter = useMemo(() => {
    return (searchParams.get("loc") || "").trim();
  }, [searchParams]);

  const isMobile = () => window.innerWidth < 992;

  const closeSidebar = () => setActiveSidebar(false);
  const toggleSidebar = () => setActiveSidebar((s) => !s);

  // ✅ body scroll lock (mobile)
  useEffect(() => {
    if (!isMobile()) return;
    document.body.style.overflow = activeSidebar ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [activeSidebar]);

  /* -------------------------------
     Fetch categories + products + random banner
  --------------------------------- */
  useEffect(() => {
    let mounted = true;

    const fetchAll = async () => {
      setError(null);

      // Categories
      try {
        setLoadingCats(true);
        const cats = await CategoriesServices.listAll();
        if (mounted) setCategories(cats || []);
      } catch {
        if (mounted) setError("Failed to load categories.");
      } finally {
        if (mounted) setLoadingCats(false);
      }

      // Products
      try {
        setLoadingProducts(true);
        const prods = await ProductServices.getAllProducts(null);
        if (mounted) setProductsRaw(prods || []);
      } catch {
        if (mounted) setError((prev) => prev || "Failed to load products.");
      } finally {
        if (mounted) setLoadingProducts(false);
      }

      // ✅ Random Shop Banner
      try {
        setLoadingBanner(true);
        const banner = await ShopPageBannerServices.getRandom();
        if (mounted) setShopBanner(banner);
      } catch (e) {
        console.error("Failed to load shop banner:", e);
        if (mounted) setShopBanner(null);
      } finally {
        if (mounted) setLoadingBanner(false);
      }
    };

    fetchAll();
    return () => {
      mounted = false;
    };
  }, []);

  /* -------------------------------
     Build ProductCard-ready data
  --------------------------------- */
  // In ShopSection.jsx — replace the products useMemo
  const products = useMemo(() => {
    const selectedLocationName = localStorage.getItem("selectedLocation");
    const locationsList = (() => {
      try {
        return JSON.parse(localStorage.getItem("locationsList") || "[]");
      } catch {
        return [];
      }
    })();

    const activeLocationId = locationsList.find(
      (loc) =>
        String(loc?.name || "").toLowerCase() ===
        String(selectedLocationName || "").toLowerCase()
    )?.id ?? null;

    return (productsRaw || []).map((p) => {
      const productLocations = Array.isArray(p?.locations) ? p.locations : [];

      const matchedLoc = locationFilter
        ? productLocations.find(
            (loc) =>
              String(loc?.name || "").toLowerCase() ===
              locationFilter.toLowerCase()
          )
        : productLocations[0] ?? null;

      const activeLocData = activeLocationId
        ? productLocations.find((loc) => Number(loc?.id) === Number(activeLocationId))
        : matchedLoc;

      const priceSource = activeLocData || matchedLoc || null;
      const locationProductPriceId = priceSource?.location_product_price_id ?? null;

      const currencySymbol = priceSource?.currency?.symbol || "$";

      const fmt = (value) => {
        if (value === null || value === undefined || value === "") return null;
        const n = Number(value);
        if (Number.isNaN(n)) return null;
        return `${currencySymbol}${n.toFixed(2)}`;
      };

      const regular = Number(
        priceSource?.price_regular ??
        p?.base_regular_price ??
        0
      );

      const baseCurrent = Number(
        priceSource?.price_current ??
        p?.base_current_price ??
        0
      );

      const flashPrice =
        priceSource?.flash_sales_price !== null &&
        priceSource?.flash_sales_price !== undefined &&
        priceSource?.flash_sales_price !== ""
          ? Number(priceSource.flash_sales_price)
          : null;

      const isOnFlashSale =
        flashPrice !== null &&
        !Number.isNaN(flashPrice) &&
        flashPrice >= 0;

      const currentPrice = isOnFlashSale ? flashPrice : baseCurrent;
      const oldPrice = isOnFlashSale ? baseCurrent : regular;

      const discountPercent = isOnFlashSale
        ? calcDiscountPercent(baseCurrent, flashPrice)
        : calcDiscountPercent(regular, baseCurrent);

      return {
        imageSrc: toAbsoluteUrl(p?.image_url || p?.image),
        title: p?.title || "Untitled Product",
        category: p?.category?.name || p?.category_name || "Category",
        oldPrice: fmt(oldPrice),
        newPrice: fmt(currentPrice),
        rating: p?.avg_rating != null ? Number(p.avg_rating) : null,
        reviewCount: Number(p?.review_count ?? 0),
        discount: isOnFlashSale
          ? `🔥 ${discountPercent ? `${discountPercent}% OFF` : "SALE"}`
          : discountPercent
          ? `${discountPercent}% OFF`
          : null,
        badgeText: !discountPercent && !isOnFlashSale ? p?.badge_text || null : null,
        badgeType: p?.badge_type || null,
        inStock: p?.in_stock !== undefined ? Boolean(p.in_stock) : true,
        id: p?.id,
        category_id: Number(p?.category_id || p?.category?.id || 0),
        price_number: currentPrice,
        locations: productLocations,
        locationProductPriceId,
      };
    });
  }, [productsRaw, locationFilter]);

  /* -------------------------------
     ✅ Category counts
  --------------------------------- */
  const categoryCounts = useMemo(() => {
    const map = {};
    for (const p of products) {
      const cid = Number(p.category_id || 0);
      if (!cid) continue;
      map[cid] = (map[cid] || 0) + 1;
    }
    return map;
  }, [products]);

  /* -------------------------------
     ✅ Parent + Child filter support
  --------------------------------- */
  const selectedCategoryObj = useMemo(() => {
    if (!selectedCategoryId) return null;
    return (
      (categories || []).find(
        (c) => Number(c?.id) === Number(selectedCategoryId)
      ) || null
    );
  }, [categories, selectedCategoryId]);

  const allowedCategoryIds = useMemo(() => {
    if (!selectedCategoryId) return null;

    const childIds = (selectedCategoryObj?.children || [])
      .map((ch) => Number(ch?.id))
      .filter(Boolean);

    return new Set([Number(selectedCategoryId), ...childIds]);
  }, [selectedCategoryId, selectedCategoryObj]);

  /* -------------------------------
     ✅ Filter + Sorting
  --------------------------------- */
  const filteredProducts = useMemo(() => {
    const q = searchQuery;

    const list = (products || []).filter((p) => {
      // Category filter
      if (allowedCategoryIds && !allowedCategoryIds.has(Number(p.category_id)))
        return false;

      // Search filter
      if (q) {
        const title = String(p.title || "").toLowerCase();
        const category = String(p.category || "").toLowerCase();
        if (!title.includes(q) && !category.includes(q)) return false;
      }

      // ✅ Location filter — matches product's assigned locations
      if (locationFilter) {
        const productLocations = Array.isArray(p.locations) ? p.locations : [];
        const hasLocation = productLocations.some(
          (loc) =>
            String(loc?.name || "").toLowerCase() ===
            locationFilter.toLowerCase()
        );
        if (!hasLocation) return false;
      }

      return true;
    });

    if (priceSort === "low")
      return [...list].sort((a, b) => (a.price_number || 0) - (b.price_number || 0));
    if (priceSort === "high")
      return [...list].sort((a, b) => (b.price_number || 0) - (a.price_number || 0));
    return list;
  }, [products, allowedCategoryIds, priceSort, searchQuery, locationFilter]);

  /* -------------------------------
     Pagination
  --------------------------------- */
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * PER_PAGE;
    return filteredProducts.slice(start, start + PER_PAGE);
  }, [filteredProducts, currentPage]);

  useEffect(() => {
    setPage(1);
  }, [selectedCategoryId, priceSort, searchQuery, locationFilter]);

  const paginationPages = useMemo(() => {
    const maxButtons = 7;
    const pages = [];

    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    const half = Math.floor(maxButtons / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + maxButtons - 1);
    start = Math.max(1, end - maxButtons + 1);

    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [totalPages, currentPage]);

  // ✅ FIX: Helper to build next params — always preserves loc + q
  const buildNextParams = ({ cat, clearCat = false } = {}) => {
    const next = new URLSearchParams();

    // preserve loc (location filter from header — must never be dropped)
    const loc = searchParams.get("loc");
    if (loc) next.set("loc", loc);

    // preserve search query
    const q = searchParams.get("q");
    if (q) next.set("q", q);

    // cat
    if (!clearCat && cat != null) next.set("cat", String(cat));

    return next;
  };

  // ✅ FIX: preserves loc + q when clearing category
  const clearCategory = () => {
    const next = buildNextParams({ clearCat: true });
    setSearchParams(next, { replace: true });
    if (isMobile()) closeSidebar();
  };

  // ✅ FIX: preserves loc + q when switching category
  const onCategoryClick = (catId) => {
    const next = buildNextParams({ cat: catId });
    setSearchParams(next, { replace: true });
    if (isMobile()) closeSidebar();
  };

  const isLoading = loadingCats || loadingProducts;

  // ✅ final banner src with fallback
  const bannerSrc = shopBanner?.image_url
    ? toAbsoluteUrl(shopBanner.image_url)
    : null;

  return (
    <section className="shop py-80">
      <div ref={shopTopRef} />

      {/* ✅ Internal CSS (mobile fixes only) */}
      <style>{`
        .shop .side-overlay{ z-index: 9998 !important; }
        .shop .shop-sidebar{ z-index: 9999 !important; }

        .shop .shopFilterBtnFix{
          display: none !important;
          align-items: center !important;
          justify-content: center !important;
          height: 40px !important;
          padding: 0 14px !important;
          white-space: nowrap !important;
          position: relative !important;
          z-index: 1 !important;
          visibility: visible !important;
          opacity: 1 !important;
        }

        @media (max-width: 991.98px){
          .shop .shopFilterBtnFix{ display: inline-flex !important; }
          .shop .flex-between,
          .shop .position-relative{ overflow: visible !important; }
        }

        @media (max-width: 575.98px) {
          .shop { padding-top: 40px !important; padding-bottom: 40px !important; }
          .shop .text-md { font-size: 12px !important; }
          .shop .text-sm { font-size: 12px !important; }

          .shop .shop-sort-select {
            min-width: 150px !important;
            height: 40px !important;
            font-size: 12px !important;
            padding: 6px 10px !important;
          }

          .shop .pagination .page-link {
            width: 44px !important;
            height: 44px !important;
            font-size: 12px !important;
            border-radius: 10px !important;
            white-space: nowrap !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            line-height: 1 !important;
            padding: 0 !important;
          }
        }

        @media (max-width: 400px) {
          .shop .shop-sort-select { min-width: 140px !important; }
          .shop .pagination .page-link { width: 40px !important; height: 40px !important; }
        }
      `}</style>

      {/* overlay */}
      <div
        className={`side-overlay ${activeSidebar ? "show" : ""}`}
        onClick={closeSidebar}
      />

      <div className="container container-lg">
        {error && (
          <div className="alert alert-danger mb-24" role="alert">
            {error}
          </div>
        )}

        <div className="row">
          {/* Sidebar */}
          <div className="col-lg-3">
            <div className={`shop-sidebar ${activeSidebar ? "active" : ""}`}>
              <button
                onClick={closeSidebar}
                type="button"
                className="shop-sidebar__close d-lg-none d-flex w-32 h-32 flex-center border border-gray-100 rounded-circle hover-bg-main-600 position-absolute inset-inline-end-0 me-10 mt-8 hover-text-white hover-border-main-600"
              >
                <i className="ph ph-x" />
              </button>

              {/* ✅ Active location filter indicator */}
              {locationFilter && (
                <div className="shop-sidebar__box border border-gray-100 rounded-8 p-20 mb-32 flex-align gap-8">
                  <i className="ph ph-map-pin text-main-600 text-xl" />
                  <span className="text-gray-900 text-sm fw-medium flex-grow-1">
                    {locationFilter}
                  </span>
                  <button
                    type="button"
                    title="Change location in the header to filter differently"
                    className="text-gray-400 text-xl bg-transparent border-0 p-0 lh-1"
                    style={{ cursor: "default" }}
                  >
                    <i className="ph ph-info" />
                  </button>
                </div>
              )}

              <div className="shop-sidebar__box border border-gray-100 rounded-8 p-32 mb-32">
                <h6 className="text-xl border-bottom border-gray-100 pb-24 mb-24">
                  Product Category
                </h6>

                {loadingCats ? (
                  <div className="text-gray-600">Loading categories...</div>
                ) : (
                  <ul className="max-h-540 overflow-y-auto scroll-sm">
                    <li className="mb-24">
                      <Link
                        to="#"
                        onClick={(e) => {
                          e.preventDefault();
                          clearCategory();
                        }}
                        className={`text-gray-900 hover-text-main-600 ${
                          !selectedCategoryId ? "text-main-600 fw-semibold" : ""
                        }`}
                      >
                        All ({products.length})
                      </Link>
                    </li>

                    {categories.map((cat, index) => {
                      const isLast = index === categories.length - 1;
                      const count = categoryCounts[cat.id] || 0;
                      const active = selectedCategoryId === Number(cat.id);

                      return (
                        <li key={cat.id} className={isLast ? "mb-0" : "mb-24"}>
                          <Link
                            to="#"
                            onClick={(e) => {
                              e.preventDefault();
                              onCategoryClick(cat.id);
                            }}
                            className={`text-gray-900 hover-text-main-600 ${
                              active ? "text-main-600 fw-semibold" : ""
                            }`}
                          >
                            {cat.name} ({count})
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* ✅ Random Shop Page Banner */}
              {bannerSrc && (
                <div className="shop-sidebar__box rounded-8">
                  <img
                    src={bannerSrc}
                    alt="Shop Banner"
                    style={{ width: "100%", height: "auto", display: "block" }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="col-lg-9">
            {/* Top */}
            <div className="flex-between gap-16 flex-wrap mb-40">
              <span className="text-gray-900">
                Showing{" "}
                {filteredProducts.length === 0
                  ? "0"
                  : `${(currentPage - 1) * PER_PAGE + 1}-${Math.min(
                      currentPage * PER_PAGE,
                      filteredProducts.length
                    )}`}{" "}
                of {filteredProducts.length} results
                {searchQuery ? (
                  <span className="ms-8 text-gray-600">
                    (Search: "{searchQuery}")
                  </span>
                ) : null}
                {locationFilter ? (
                  <span className="ms-8 text-gray-600">
                    in{" "}
                    <span className="text-main-600 fw-medium">
                      {locationFilter}
                    </span>
                  </span>
                ) : null}
              </span>

              <div className="position-relative flex-align gap-16 flex-wrap">
                <div className="list-grid-btns flex-align gap-16">
                  <button
                    onClick={() => setGrid(true)}
                    type="button"
                    className={`w-44 h-44 flex-center border rounded-6 text-2xl list-btn border-gray-100 ${
                      grid === true
                        ? "border-main-600 text-white bg-main-600"
                        : ""
                    }`}
                  >
                    <i className="ph-bold ph-list-dashes" />
                  </button>

                  <button
                    onClick={() => setGrid(false)}
                    type="button"
                    className={`w-44 h-44 flex-center border rounded-6 text-2xl grid-btn border-gray-100 ${
                      grid === false
                        ? "border-main-600 text-white bg-main-600"
                        : ""
                    }`}
                  >
                    <i className="ph ph-squares-four" />
                  </button>
                </div>

                <div className="flex-align gap-8">
                  <span className="text-gray-900 text-sm fw-medium">
                    Sort by:
                  </span>
                  <select
                    className="form-select h-40 shop-sort-select"
                    value={priceSort}
                    onChange={(e) => setPriceSort(e.target.value)}
                    style={{ minWidth: 200 }}
                  >
                    <option value="">Default</option>
                    <option value="low">Price: Low to High</option>
                    <option value="high">Price: High to Low</option>
                  </select>

                  {/* ✅ Filters button (mobile/tablet only via CSS) */}
                  <button
                    type="button"
                    className="btn btn-secondary shopFilterBtnFix"
                    onClick={toggleSidebar}
                  >
                    Filters
                  </button>
                </div>
              </div>
            </div>

            {/* Products */}
            {isLoading ? (
              <div className="text-gray-600">Loading products...</div>
            ) : (
              <div className={`list-grid-wrapper ${grid ? "list-view" : ""}`}>
                <div className="row gy-4 g-12">
                  {paginatedProducts.length ? (
                    paginatedProducts.map((product, index) => (
                      <ProductCard key={product.id || index} {...product} />
                    ))
                  ) : (
                    <div className="col-12">
                      <div className="border border-gray-100 rounded-8 p-24 text-gray-700">
                        No products found for the selected filters.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Pagination */}
            {filteredProducts.length > 0 && totalPages > 1 && (
              <ul className="pagination flex-center flex-wrap gap-16 mt-40">
                <PaginationItem
                  page="arrow-left"
                  isArrow
                  icon="ph-arrow-left"
                  onClick={() => {
                    setPage((p) => Math.max(1, p - 1));
                    scrollToTop();
                  }}
                />

                {paginationPages.map((p) => (
                  <PaginationItem
                    key={p}
                    page={String(p).padStart(2, "0")}
                    isActive={p === currentPage}
                    onClick={() => {
                      setPage(p);
                      scrollToTop();
                    }}
                  />
                ))}

                <PaginationItem
                  page="arrow-right"
                  isArrow
                  icon="ph-arrow-right"
                  onClick={() => {
                    setPage((p) => Math.min(totalPages, p + 1));
                    scrollToTop();
                  }}
                />
              </ul>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ShopSection;